
  import { defineComponent, onMounted, ref, computed, watch } from 'vue';
  import { ErrorMessage, Field, Form } from 'vee-validate';
  import { setCurrentPageBreadcrumbs } from '@/core/helpers/breadcrumb';
  import { useI18n } from 'vue-i18n/index';
  import { useStore } from 'vuex';
  import { useRouter, useRoute } from 'vue-router';
  import { Actions } from '@/store/enums/StoreEnums';
  import { useAbility } from '@casl/vue';
  import Swal from 'sweetalert2/dist/sweetalert2.js';
  import * as Yup from 'yup';
  import Multiselect from '@vueform/multiselect';

  export default defineComponent({
    name: 'ads-brokerage-editing',
    components: {
      ErrorMessage,
      Field,
      Form,
      Multiselect,
    },
    async setup() {
      const { t, te } = useI18n();
      const store = useStore();
      const router = useRouter();
      const route = useRoute();
      const { can } = useAbility();
      const submitButton = ref<HTMLButtonElement | null>(null);
      const selectedPropertyTypeIds = ref<number[]>([]);
      const selectedAreaIds = ref<number[]>([]);
      const selectedSizes = ref<number[]>([]);
      const selectedConfigId = ref<number | null>(null);
      const selectedCategoryId = ref<number | null>(null);
      const isInitializing = ref(true);

      const generateSizeOptions = () => {
        const sizes: number[] = [];
        for (let i = 400; i <= 1250; i += 50) {
          sizes.push(i);
        }
        return sizes;
      };

      const sizeOptions = ref(generateSizeOptions());

      const translate = (text: string) => {
        if (te(text)) {
          return t(text);
        } else {
          return text;
        }
      };

      const goBack = () => {
        router.back();
      };

      const slugify = (text: string): string => {
        return text
          .toString()
          .toLowerCase()
          .trim()
          .replace(/[\u064B-\u0652]/g, '')
          .replace(/[^\u0600-\u06FFa-z0-9\s-]/g, '')
          .replace(/\s+/g, '-')
          .replace(/-+/g, '-')
          .replace(/^-|-$/g, '');
      };

      const generateSlug = () => {
        let categorySlug = '';
        let configSlug = '';

        if (selectedCategoryId.value) {
          const category = store.getters.propertyAllCategoryList.find(
            (c) => c.id === selectedCategoryId.value
          );
          if (category) {
            categorySlug = slugify(category.titleEn || category.titleAr || '');
          }
        }

        if (selectedConfigId.value) {
          const config = store.getters.allAdsBrokerageConfigsList.find(
            (c) => c.id === selectedConfigId.value
          );
          if (config) {
            configSlug = config.slug || '';
          }
        }

        brokerage.value.groupSlug = `${categorySlug}-${configSlug}`.replace(
          /^-|-$/g,
          ''
        );
      };

      watch(selectedCategoryId, () => {
        if (isInitializing.value) return;
        selectedPropertyTypeIds.value = [];
        generateSlug();
      });

      watch(selectedConfigId, () => {
        if (isInitializing.value) return;
        generateSlug();
      });

      const filteredPropertyTypes = computed(() => {
        if (!selectedCategoryId.value) {
          return store.getters.allPropertyTypesList;
        }
        return store.getters.allPropertyTypesList.filter(
          (type) => type.propertyCategoryId === selectedCategoryId.value
        );
      });

      const adsBrokerageSchema = Yup.object().shape({
        configId: Yup.number()
          .required(() => translate('CONFIG_IS_REQUIRED_FIELD'))
          .label('Config'),
        groupLabel: Yup.number()
          .required(() => translate('GROUP_LABEL_IS_REQUIRED_FIELD'))
          .label('Group Label'),
        groupSlug: Yup.string()
          .required(() => translate('GROUP_SLUG_IS_REQUIRED_FIELD'))
          .label('Group Slug'),
        startingPrice: Yup.number()
          .required(() => translate('STARTING_PRICE_IS_REQUIRED_FIELD'))
          .min(0, () => translate('PRICE_MUST_BE_POSITIVE'))
          .label('Starting Price'),
        endingPrice: Yup.number()
          .required(() => translate('ENDING_PRICE_IS_REQUIRED_FIELD'))
          .min(0, () => translate('PRICE_MUST_BE_POSITIVE'))
          .moreThan(Yup.ref('startingPrice'), () =>
            translate('ENDING_PRICE_MUST_BE_GREATER')
          )
          .label('Ending Price'),
      });

      const onSubmitUpdate = async (values) => {
        if (submitButton.value) {
          submitButton.value!.disabled = true;
          submitButton.value.setAttribute('data-kt-indicator', 'on');
        }

        const selectedCategory = store.getters.propertyAllCategoryList.find(
          (c) => c.id === selectedCategoryId.value
        );
        const groupLabel =
          selectedCategory?.titleEn || selectedCategory?.titleAr || '';

        const dataToSubmit: any = {
          configId: selectedConfigId.value,
          groupLabel: groupLabel,
          groupSlug: brokerage.value.groupSlug,
          startingPrice: Number(values.startingPrice),
          endingPrice: Number(values.endingPrice),
        };

        if (selectedPropertyTypeIds.value.length > 0) {
          dataToSubmit.propertyTypeIds = selectedPropertyTypeIds.value;
        }

        if (selectedAreaIds.value.length > 0) {
          dataToSubmit.areaIds = selectedAreaIds.value;
        }

        if (selectedSizes.value.length > 0) {
          dataToSubmit.sizes = selectedSizes.value.map((s) => Number(s));
        }

        if (selectedCategoryId.value) {
          dataToSubmit.categoryId = selectedCategoryId.value;
        }

        const initialFormValues = computed(() => ({
          configId: selectedConfigId.value,
          groupLabel: selectedCategoryId.value,
          groupSlug: brokerage.value?.groupSlug,
          startingPrice: brokerage.value?.startingPrice,
          endingPrice: brokerage.value?.endingPrice,
        }));

        await store.dispatch(Actions.UPDATE_ADS_BROKERAGE, {
          id: route.params.id,
          data: dataToSubmit,
        });

        const [errorName] = Object.keys(store.getters.getErrors);
        const error = store.getters.getErrors[errorName];

        if (error) {
          Swal.fire({
            text: translate(error[0]),
            icon: 'error',
            buttonsStyling: false,
            confirmButtonText: translate('tryAgainExcl'),
            customClass: {
              confirmButton: 'btn fw-bold btn-light-danger',
            },
          });
          submitButton.value?.removeAttribute('data-kt-indicator');
          submitButton.value!.disabled = false;
        } else {
          Swal.fire({
            text: translate('SUCCESSFULLY_UPDATED_ADS_BROKERAGE'),
            icon: 'success',
            buttonsStyling: false,
            confirmButtonText: translate('ok'),
            customClass: {
              confirmButton: 'btn btn-light-primary',
            },
          }).then(() => {
            router.push({ name: 'ads-brokerage-listing' });
          });
        }
      };

      onMounted(async () => {
        setCurrentPageBreadcrumbs(translate('adsBrokerageUpdate'), [
          translate('ads'),
        ]);
        await store.dispatch(Actions.GET_ALL_PROPERTY_TYPES_LIST);
        await store.dispatch(Actions.GET_ALL_AREAS_LIST);
        await store.dispatch(Actions.GET_ALL_ADS_BROKERAGE_CONFIGS);
        await store.dispatch(
          Actions.GET_ALL_PROPERTY_CATEGORIES_BY_AD_TYPE_ID,
          {
            adType: 1,
          }
        );

        if (brokerage.value?.propertyTypeIds?.length) {
          selectedPropertyTypeIds.value =
            brokerage.value.propertyTypeIds.slice();
        }

        if (brokerage.value?.groupCategoryId) {
          selectedCategoryId.value = brokerage.value.groupCategoryId;
        }

        if (brokerage.value?.areaIds?.length) {
          selectedAreaIds.value = brokerage.value.areaIds.slice();
        }

        if (brokerage.value?.sizes?.length) {
          selectedSizes.value = brokerage.value.sizes.slice();
        }

        isInitializing.value = false;
      });

      // Fetch the brokerage data
      const { data } = await store.dispatch(
        Actions.GET_ADS_BROKERAGE,
        route.params.id
      );

      const brokerage = ref<any>(data.data);

      if (brokerage.value?.propertyTypeIds) {
        selectedPropertyTypeIds.value = brokerage.value.propertyTypeIds.slice();
      }

      if (brokerage.value?.areaIds) {
        selectedAreaIds.value = brokerage.value.areaIds.slice();
      }

      if (brokerage.value?.sizes) {
        selectedSizes.value = brokerage.value.sizes.slice();
      }

      if (brokerage.value?.configId) {
        selectedConfigId.value = brokerage.value.configId;
      }

      if (brokerage.value?.categoryId) {
        selectedCategoryId.value = brokerage.value.categoryId;
      }

      const initialFormValues = computed(() => ({
        configId: selectedConfigId.value,
        groupLabel: selectedCategoryId.value,
        groupSlug: brokerage.value?.groupSlug,
        startingPrice: brokerage.value?.startingPrice,
        endingPrice: brokerage.value?.endingPrice,
      }));

      store.dispatch(Actions.REMOVE_BODY_CLASSNAME, 'page-loading-over');

      return {
        onSubmitUpdate,
        submitButton,
        translate,
        adsBrokerageSchema,
        goBack,
        brokerage,
        can,
        selectedPropertyTypeIds,
        selectedAreaIds,
        selectedSizes,
        sizeOptions,
        propertyTypes: computed(() => store.getters.allPropertyTypesList),
        areas: computed(() => store.getters.allListAreas),
        configs: computed(() => store.getters.allAdsBrokerageConfigsList),
        propertyTypesCategories: computed(
          () => store.getters.propertyAllCategoryList
        ),
        selectedConfigId,
        selectedCategoryId,
        slugify,
        filteredPropertyTypes,
        initialFormValues,
      };
    },
  });
